import { logger } from '../../../utils/logger'
import type { ServiceHandler } from './service-registry'
import { validateUrl } from './validateUrl'

/**
 * REDIRECT service handler
 *
 * Handles requests to redirect to any http/https URL.
 * Validates that a URL is provided before performing the redirect.
 *
 * Expected payload: string (URL) or { url: string }
 * Expected response format:
 * - Success: { redirected: true, url: string }
 * - Error: "error message"
 */
export const redirect: ServiceHandler = async ({
  iframeId,
  messageId,
  payload,
  sendSuccessResponse,
  sendErrorResponse,
}) => {
  try {
    // Validate that URL is provided and is a string
    let url: string

    if (typeof payload === 'string') {
      url = payload
    } else if (
      payload &&
      typeof payload === 'object' &&
      'url' in payload &&
      typeof payload.url === 'string'
    ) {
      url = payload.url
    } else {
      sendErrorResponse(iframeId, messageId, 'Missing URL for redirect')
      return
    }

    // Validate URL format and security using validator package
    const validation = validateUrl(url)

    if (!validation.isValid) {
      logger('warn', 'CustomEmbeddedCode:redirect: Blocked dangerous URL scheme attempt', {
        context: { iframeId, messageId, payload, blockedUrl: url },
      })
      sendErrorResponse(iframeId, messageId, 'Invalid URL format')
      return
    }

    // Perform the redirect
    window.location.href = url

    // Note: This response may not be received due to the redirect
    sendSuccessResponse(iframeId, messageId, { redirected: true, url })
  } catch (error) {
    logger(
      'error',
      new Error('CustomEmbeddedCode:redirect: Error performing redirect', { cause: error }),
      {
        context: { iframeId, messageId, payload },
      },
    )
    sendErrorResponse(iframeId, messageId, 'Unable to perform redirect')
  }
}
